<template>
  <div class="card card-white">
    <BaseDataTable
      :entries="tableData.rows"
      :columns="tableData.columns"
      :loading="loading"
      :paginator-info="paginatorInfo"
      class="f-12"
      :search-query="searchQuery"
      force-search-in-api-mode
      @search="updatePagination"
    >
      <template #control-bar="{ updateSearch, search }">
        <slot name="header" :update-search="updateSearch" :search="search"></slot>
      </template>
      <template #no-results="{ loading, search }">
        <WdsStack direction-row justify-center>
          <BaseLoadingNotice v-if="loading" class="tw-z-10">
            <template #main>{{ $t('Finding reviewed approvals') }}</template>
          </BaseLoadingNotice>
          <template v-else-if="!!search">
            <WdsStack class="tw-py-10" direction-column justify-center align-center gap-sm>
              <WdsText large>
                {{ $t('No Matching Results ') }}
              </WdsText>
              <WdsText secondary>
                {{
                  $t(
                    "We couldn't find any requests matching your search or filters. Please try adjusting your search terms or filter criteria.",
                  )
                }}
              </WdsText>
            </WdsStack>
          </template>
          <template v-else>
            <WdsStack class="tw-py-10" direction-column justify-center align-center gap-sm>
              <div class="icon-inner tw-w-fit">
                <WdsIcon name="stamp" weight="thick" color="#5D36F1" :size="24"></WdsIcon>
              </div>
              <WdsText large>
                {{ $t('There are no past reviewed requests') }}
              </WdsText>
              <WdsText secondary>
                {{ $t('Change view to “Pending” to see active requests.') }}
              </WdsText>
            </WdsStack>
          </template>
        </WdsStack>
      </template>
      <template #status="{ row }">
        <WdsTag
          sm
          class="!tw-rounded"
          :success="row.state === ApprovalApprovableState.Approved"
          :error="
            row.state === ApprovalApprovableState.Rejected ||
            row.state === ApprovalApprovableState.Cancelled
          "
        >
          {{ getApprovalStateStatusLabel(row.state) }}
        </WdsTag>
      </template>
      <template #Reviewer="{ row }">
        <WdsAvatarLabel
          :text="row.actionedBy.name"
          :path="row.actionedBy.avatar"
          :initials="textHelpers.nameStringToInitials(row.actionedBy.name, 2)"
          truncated
          sm
        >
          <template #subText>
            <WdsTag sm>
              {{ row.approver.userGroup.name }}
            </WdsTag>
          </template>
        </WdsAvatarLabel>
      </template>
      <template #Action="{ row }">
        <WdsStack class="tw-pr-2" direction-row justify-end align-center gap-sm>
          <WdsMenu placement="bottom-end" global>
            <template #trigger>
              <WdsButtonGhost>
                <WdsIcon name="overflow-menu-horizontal" :size="16" />
              </WdsButtonGhost>
            </template>
            <WdsButtonGhost
              class="tw-mx-2"
              target="_blank"
              :href="
                laravelNamedRoute('hires.hire.view', {
                  hire: row.approvalApprovable?.approvable?.id,
                  tab: 'approvals',
                })
              "
              @click.stop
            >
              <WdsMenuOption>
                <WdsIcon :size="16" name="stamp" />
                {{ $t('View approval details') }}
              </WdsMenuOption>
            </WdsButtonGhost>
          </WdsMenu>
        </WdsStack>
      </template>
    </BaseDataTable>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, ComputedRef, markRaw, reactive } from 'vue'
import {
  WdsAvatarLabel,
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import { graphql, useFragment } from '@/gql'
import BaseDataTable from '@/components/BaseDataTable'
import { TableDataHandler } from '@/vue-prop-types/DataTable'
import { PaginatorInfoFragment } from '@/components/GraphQL/fragments/paginatorInfoFragment'
import { TdText } from '@/components/BaseDataTable/Cells'
import { useLocaleTimeAgo } from '@/composables/useLocaleTimeAgo'
import { ApprovalApprovableState } from '@/gql/graphql'
import { textHelpers } from '@/utils/tools'
import { getApprovalStateStatusLabel } from '@/components/Workflows/Approvals/utilities/statusFormatting'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import BaseLoadingNotice from '@/components/BaseLoadingNotice.vue'
import ApprovalDisplayForTable from '@/components/Workflows/Approvals/Triggers/ApprovalDisplayForTable.vue'

const props = withDefaults(defineProps<{ searchQuery: string; accounts: string[] }>(), {
  searchQuery: '',
})

const paginationSettings = reactive({
  page: 1,
  displayPerPage: 1000,
})

const haveMultipleAccounts: ComputedRef<boolean> = computed(() => props.accounts.length > 1)

const { result, loading } = useQuery(
  graphql(/* GraphQL */ `
    query TableReviewedApprovals(
      $page: Int
      $perPage: Int
      $accounts: [ID!]
      $users: [ID!]
      $status: [ApprovalApprovableState!]
    ) {
      approvalStates(
        page: $page
        first: $perPage
        accounts: $accounts
        users: $users
        status: $status
      ) {
        data {
          id
          state
          approver {
            position
            userGroup {
              name
            }
            approvalRule {
              approverCount
            }
          }
          actionedBy {
            name
            avatar
          }
          approvalApprovable {
            id
            approval {
              trigger
            }
            approvable {
              ... on Hire {
                ...HireApprovableFragment
              }
            }
          }
          createdAt
        }
        paginatorInfo {
          ...PaginatorInfoFragment
          count
          currentPage
          total
        }
      }
    }
  `),
  () => ({
    accounts: props.accounts,
    users: [currentUser.globalId],
    status: [
      ApprovalApprovableState.Approved,
      ApprovalApprovableState.Rejected,
      ApprovalApprovableState.NeedsChange,
      ApprovalApprovableState.Cancelled,
    ],
    page: paginationSettings.page,
    perPage: paginationSettings.displayPerPage,
  }),
  {
    clientId: 'publicApiCustomFields',
    debounce: 100,
  },
)

const paginatorInfo = computed(() =>
  useFragment(PaginatorInfoFragment, result.value?.approvalStates?.paginatorInfo),
)

const tableData = computed(
  () =>
    new TableDataHandler({
      rows: result.value?.approvalStates?.data || [],
      columns: [
        {
          title: $t('Review date'),
          key: 'createdAt',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1 nowrap': true },
          order: 'desc',
          renderUsing: {
            component: markRaw(WdsText),
            componentProps: (row) => ({
              text: useLocaleTimeAgo(row.createdAt).value,
              secondary: true,
            }),
          },
          convert(row) {
            return new Date(row.createdAt)
          },
        },
        {
          title: $t('Reviewed as'),
          slot: 'status',
          key: 'status',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1 nowrap': true },
          getFn: (row) => getApprovalStateStatusLabel(row.state),
        },
        {
          title: $t('Workflow'),
          key: 'approval.trigger',
          class: 'nowrap',
          renderUsing: {
            component: markRaw(ApprovalDisplayForTable),
            componentProps: (row) => ({
              trigger: row.approvalApprovable.approval.trigger,
              workflowObject: row.approvalApprovable,
            }),
          },
          searchKeys: ['approvable.worker.name', 'approvable.job.name', 'approval.trigger'],
        },
        {
          title: $t('Step number'),
          key: 'position',
          class: 'nowrap tw-max-w-fit tw-text-center',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          orderAble: false,
          renderUsing: {
            component: markRaw(TdText),
            componentProps: (row) => ({
              html: `<strong>${row.approver.position}</strong><span class="tw-text-gray-700">/${row.approver.approvalRule.approverCount}</span>`,
            }),
          },
          convert: (row) => row.approver.position,
        },
        {
          title: $t('Reviewer'),
          key: 'approver',
          slot: 'Reviewer',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          convert: (row) => row.approver.userGroup.name,
        },
        {
          title: $t('Company'),
          key: 'company.id',
          class: 'nowrap tw-max-w-fit tw-text-center',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          orderAble: false,
          renderUsing: {
            component: markRaw(TdText),
            componentProps: (row) => ({
              html: `
                <strong
                style="display: inline-block; max-width: 300px; text-overflow:ellipsis; overflow:hidden; white-space: nowrap; font-weight: 500;"
                title="${row?.approvalApprovable?.approvable?.company?.name ?? ''}"
              >
                ${row?.approvalApprovable?.approvable?.company?.name ?? ''}
              </strong>`,
            }),
          },
        },
        {
          title: $t('Requested'),
          key: 'requested',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          sort: 'actionedBy.name',
          renderUsing: {
            component: markRaw(WdsText),
            componentProps: (row) => ({
              text: row.actionedBy.name,
              subText: useLocaleTimeAgo(row.createdAt).value,
            }),
          },
          convert: (row) => row.actionedBy.name,
        },
        {
          title: '',
          slot: 'Action',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          key: 'action',
          orderAble: false,
        },
      ].filter((col) => col.key !== 'company.id' || haveMultipleAccounts.value),
    }),
)

function updatePagination(options: any) {
  paginationSettings.displayPerPage = options.perPage
  paginationSettings.page = options.page + 1
}
</script>

<style lang="scss" scoped>
.icon-inner {
  padding: 5px;
  border-radius: 50%;
  border: 6px solid #f3f0ff;
  background-color: #e8e2ff;
  &:deep(.wds-icon-stamp) {
    path {
      stroke: #5d36f1;
      stroke-width: 1px;
    }
  }
}
</style>
